import { Flex } from '@ui-library/misc';
import { apiAuthCaller } from '@services/apiCaller';
import useStores from '@stores/useStores';
import exportResponseError from '@utils/exportResponseError';
import { SmartRetry } from '@utils/retry';
import axios from 'axios';
import React from 'react';
import { SubHeader } from '@components/sources/source/warehouseSource/configureData/style';
import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Skeleton } from 'antd';
import Button from '@ui-library/button';
import { MetaData } from '@ui-library/dynamicSelect';
import AlertMessage from './AlertMessage';
import Input from '@ui-library/input';
import ResourceSelectionTree from './resourceTreeSelect';
import { Resource } from '../ResourceSelection';

export type ColumnSelection = { [i: string]: { selected: boolean } };
interface ResourceColumn {
  name: string;
  systemColumn?: boolean;
}
const retry = new SmartRetry<{ data: { columns: ResourceColumn[] } }>({ times: 2 });
interface ColumnSelectionViewProps {
  defaultValue?: { [i: string]: ColumnSelection };
  onChange?: (columnSelection: { [i: string]: ColumnSelection }) => void;
  onChangeDisabledStatus?: (disabled: boolean) => void;
  sourceRole: string;
  resourceRole: string;
  resources: Resource[];
  metadata?: MetaData;
  disabled?: boolean;
}
const ColumnSelectionView = ({
  defaultValue,
  resourceRole,
  sourceRole,
  resources,
  onChange,
  metadata,
  onChangeDisabledStatus,
  ...props
}: ColumnSelectionViewProps) => {
  const { userStore } = useStores();
  const [columns, setColumns] = React.useState<ResourceColumn[]>([]);
  const [keyword, setKeyword] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const [currentResource, setCurrentResource] = React.useState(resourceRole);
  const [selectedColumns, setSelectedColumns] = React.useState(defaultValue || {});
  const [loading, setLoading] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);

  const isAllSelected = Object.keys(selectedColumns[currentResource] || {}).every(
    (key) => selectedColumns[currentResource] && selectedColumns[currentResource][key]?.selected,
  );
  const isIndeterminateSelected =
    !isAllSelected &&
    Object.keys(selectedColumns[currentResource] || {}).some(
      (key) => selectedColumns[currentResource] && selectedColumns[currentResource][key]?.selected,
    );

  React.useEffect(() => {
    setLoading(true);
    setError('');
    retry
      .exec(() =>
        apiAuthCaller(userStore.token, false, 25000).post(
          `/cloudSources/info/roles/${sourceRole}/resources/${currentResource}/columns`,
          metadata?.params,
          {
            cancelToken: new axios.CancelToken(retry.setCancelMethod),
          },
        ),
      )
      .then((resp) => {
        const columns = resp?.data?.columns;
        setColumns(columns);
        const defaultColumnsSelection = columns.reduce((accum, col) => {
          accum[col.name] = { selected: true };
          return accum;
        }, {} as ColumnSelection);
        setSelectedColumns({
          ...selectedColumns,
          [currentResource]: {
            ...defaultColumnsSelection,
            ...selectedColumns[currentResource],
          },
        });
      })
      .catch((err) => {
        if (err.constructor.name === 'Cancel') {
          return;
        }
        const error = exportResponseError(err);
        setError(error?.message);
      })
      .finally(() => {
        setLoading(false);
      });

    return retry.cancel;
  }, [currentResource, sourceRole, retryCount]);
  const disabled = loading || !!error;
  React.useEffect(() => {
    if (onChangeDisabledStatus) {
      onChangeDisabledStatus(disabled);
    }
  }, [disabled, onChangeDisabledStatus]);
  const onChangeResource = React.useCallback((resource: string) => {
    setCurrentResource(resource);
  }, []);
  React.useEffect(() => {
    if (onChange) {
      onChange(selectedColumns);
    }
  }, [selectedColumns, onChange]);
  const onRetry = React.useCallback(() => {
    setRetryCount(retryCount + 1);
  }, [retryCount]);
  const handleOnAllChange = React.useCallback(
    (checked) => {
      let defaultColumnsSelection;
      if (checked) {
        defaultColumnsSelection = columns.reduce((accum, col) => {
          accum[col.name] = { selected: true };
          return accum;
        }, {} as ColumnSelection);
      } else {
        defaultColumnsSelection = columns.reduce((accum, col) => {
          accum[col.name] = { selected: !!col.systemColumn };
          return accum;
        }, {} as ColumnSelection);
      }
      setSelectedColumns({
        ...selectedColumns,
        [currentResource]: {
          ...defaultColumnsSelection,
        },
      });
    },
    [selectedColumns],
  );
  const visibleColumns = keyword
    ? columns?.filter((col) => col.name.toLowerCase().includes(keyword.toLowerCase()))
    : columns;
  return (
    <div>
      <div className="column_selection__body">
        <div style={{ margin: '16px 0 24px 0' }} className="column_selection__resource_container">
          <SubHeader>Select Resource</SubHeader>
          <ResourceSelectionTree
            loading={loading}
            disabled={(disabled && !error) || props.disabled}
            defaultValue={resourceRole}
            resources={resources}
            onChange={onChangeResource}
          />
        </div>
        <Flex
          alignItems="center"
          className="column_selection__cta_container"
          style={{ marginBottom: 16 }}
        >
          <div className="column_selection__search_container">
            <Input
              disabled={disabled}
              allowClear
              placeholder="Search column"
              onChange={(e) => setKeyword(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </div>
          <div style={{ marginLeft: 'auto' }} className="column_selection__select_all_container">
            <Checkbox
              disabled={disabled}
              style={{ fontWeight: 700, fontSize: 12 }}
              onChange={(e) => handleOnAllChange(e.target.checked)}
              checked={isAllSelected}
              indeterminate={isIndeterminateSelected}
            >
              SELECT ALL
            </Checkbox>
          </div>
          {!props.disabled ? (
            <div
              className="column_selection__select_all_button_container"
              style={{ display: 'none' }}
            >
              <Button
                secondary
                onClick={() => handleOnAllChange(!isAllSelected)}
                name={isAllSelected ? 'Unselect all' : 'Select all'}
              />
            </div>
          ) : null}
        </Flex>
        <div
          style={{
            maxHeight: 400,
            minHeight: 400,
            overflowY: 'auto',
            paddingRight: 8,
          }}
          className="column_selection__columns_container"
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {loading ? (
            <Skeleton active paragraph={{ rows: 10 }} />
          ) : error ? (
            <AlertMessage
              message="We failed to fetch columns"
              showIcon
              description={error}
              type="error"
              action={<Button onClick={onRetry} size="small" danger name="Retry" />}
            />
          ) : (
            visibleColumns.map((column) => (
              <Flex
                centered
                style={{ marginBottom: 12 }}
                className="column_selection__row_container"
              >
                <div>{column.name}</div>
                <div style={{ marginLeft: 'auto' }}>
                  <Checkbox
                    disabled={!!column.systemColumn || props.disabled}
                    onChange={(e) => {
                      setSelectedColumns({
                        ...selectedColumns,
                        [currentResource]: {
                          ...selectedColumns[currentResource],
                          [column.name]: { selected: e.target.checked },
                        },
                      });
                    }}
                    checked={
                      selectedColumns[currentResource] &&
                      selectedColumns[currentResource][column.name]?.selected
                    }
                  />
                </div>
              </Flex>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ColumnSelectionView;
